import {
	isArray, isFunction, isNumber, isObject, isNullOrUndefined
} from 'library/ts/utilities/modules/Type';
import { playerServicesUUID } from 'library/ts/utilities/modules/String';
import { IConveyor } from './interfaces/IConveyor';
import { IConveyorFactory } from './interfaces/IConveyorFactory';
import { IConveyorResult } from './interfaces/IConveyorResult';
import { IProcessSettings } from './interfaces/IProcessSettings';
import { ITransformerEntry } from './interfaces/ITransformerEntry';
import { ITransformerOptions } from './interfaces/ITransformerOptions';

/**
 * @copyright © Paramount Streaming
 * @description Conveyor module - Object transformation pipeline system
 * Allows disparate code modules to cooperate in transforming objects without direct knowledge of each other
 */
class ConveyorClass implements IConveyor {
	/**
	 * @description Internal registry storing transformers by key
	 */
	private static registry: Map<string, ITransformerEntry[]> = new Map();

	/**
	 * @description Key identifier for this Conveyor instance
	 */
	private key: string;

	/**
	 * @constructor
	 * @param {string} key Identifier for this transformer pipeline
	 */
	constructor( key: string ) {
		this.key = key;
	}

	/**
	 * @description Generates a unique ID for transformers
	 * @returns {string} Unique identifier
	 */
	private static generateId(): string {
		return `transformer_${playerServicesUUID()}`;
	}

	/**
	 * @description Checks if a transformer has expired based on its options
	 * @param {ITransformerEntry} entry Transformer entry to check
	 * @returns {boolean} True if expired, false otherwise
	 */
	private static isExpired( entry: ITransformerEntry ): boolean {
		const { options, invocations } = entry;

		// Check time-based expiration
		if ( isNumber( options.expiresAt ) && options.expiresAt > 0 && Date.now() >= options.expiresAt ) {
			return true;
		}

		// Check invocation-based expiration
		if ( isNumber( options.expiresAfter ) && options.expiresAfter > 0 && invocations >= options.expiresAfter ) {
			return true;
		}

		return false;
	}

	/**
	 * @description Removes expired transformers from the registry for a given key
	 * @param {string} key Registry key to clean
	 */
	private static cleanExpiredTransformers( key: string ): void {
		const transformers = ConveyorClass.registry.get( key );
		if ( !transformers ) {
			return;
		}

		const active = transformers.filter( ( entry ) => !ConveyorClass.isExpired( entry ) );

		if ( active.length === 0 ) {
			ConveyorClass.registry.delete( key );
		} else if ( active.length !== transformers.length ) {
			ConveyorClass.registry.set( key, active );
		} else {
			// All transformers are still active, no changes needed
			return;
		}
	}

	/**
	 * @description Registers a transformer function for this key
	 * @param {Function} fn Transformer function (sync or async)
	 * @param {ITransformerOptions} options Optional configuration
	 * @returns {Function} Deregister function to remove this transformer
	 */
	public transformer<T = unknown>(
		fn: ( obj: T ) => T | Promise<T>,
		options: ITransformerOptions = {}
	): ( () => void ) {
		// Validate transformer function
		if ( isFunction( fn ) === false ) {
			throw new Error( 'Conveyor: transformer must be a function' );
		}

		// Create transformer entry
		const entry: ITransformerEntry<T> = {
			fn,
			options: {
				priority: options.priority !== undefined ? options.priority : 0,
				expiresAfter: options.expiresAfter,
				expiresAt: options.expiresAt
			},
			invocations: 0,
			id: ConveyorClass.generateId()
		};

		// Get or create transformers array for this key
		if ( !ConveyorClass.registry.has( this.key ) ) {
			ConveyorClass.registry.set( this.key, [] );
		}

		const currentTransformers = ConveyorClass.registry.get( this.key );
		if ( currentTransformers ) {
			currentTransformers.push( entry );

			// Sort by priority (higher priority first)
			currentTransformers.sort( ( a, b ) => ( b.options.priority ?? 0 ) - ( a.options.priority ?? 0 ) );
		}

		// Return deregister function
		return (): void => {
			const deregisterTransformers = ConveyorClass.registry.get( this.key );
			if ( !isArray( deregisterTransformers ) ) {
				return;
			}

			const index = deregisterTransformers.findIndex( ( t ) => t.id === entry.id );
			if ( isNumber( index ) === true && index !== -1 ) {
				deregisterTransformers.splice( index, 1 );

				// Clean up empty registry entry
				if ( deregisterTransformers.length === 0 ) {
					ConveyorClass.registry.delete( this.key );
				}
			}
		};
	}

	/**
	 * @description Handles error results from transformers
	 * @param {IConveyorResult} errorResult Error result object
	 * @param {IProcessSettings} settings Processing settings
	 * @returns {boolean} True if processing should stop, false to continue
	 */
	private handleTransformerError( errorResult: IConveyorResult, settings?: IProcessSettings ): boolean {
		if ( !isNullOrUndefined( settings?.onError ) && isFunction( settings.onError ) ) {
			return settings.onError( errorResult ) === true;
		}
		return false;
	}

	/**
	 * @description Processes an object through all registered transformers for this key
	 * @param {T} object Object to transform
	 * @param {IProcessSettings} settings Optional processing settings
	 * @returns {Promise<T>} Transformed object or error result
	 */
	public async process<T = unknown>( object: T, settings?: IProcessSettings ): Promise<T> {
		// Clean expired transformers first
		ConveyorClass.cleanExpiredTransformers( this.key );

		const transformers = ConveyorClass.registry.get( this.key );

		// If no transformers registered, return object unchanged
		if ( !transformers || transformers.length === 0 ) {
			return object;
		}

		let transformed = object;

		// Execute transformers in priority order
		for ( const entry of transformers ) {
			const transformResult = await this.executeTransformer( entry, transformed, settings );

			if ( transformResult.shouldStop ) {
				break;
			}

			if ( transformResult.success ) {
				transformed = transformResult.value;
			}
		}

		// Final cleanup of expired transformers after processing
		ConveyorClass.cleanExpiredTransformers( this.key );

		return transformed;
	}

	/**
	 * @description Executes a single transformer and handles its result
	 * @param {ITransformerEntry} entry Transformer entry to execute
	 * @param {T} transformed Current transformed object
	 * @param {IProcessSettings} settings Processing settings
	 * @returns {Promise<{success: boolean, value: T, shouldStop: boolean}>} Execution result
	 */
	private async executeTransformer<T>(
		entry: ITransformerEntry,
		transformed: T,
		settings?: IProcessSettings
	): Promise<{ success: boolean; value: T; shouldStop: boolean }> {
		try {
			// Increment invocation count
			entry.invocations++;

			// Execute transformer (handle both sync and async)
			const result = await Promise.resolve( entry.fn( transformed ) );

			// Check if transformer returned an error
			const isErrorResult = !isNullOrUndefined( result ) && isObject( result ) && ( result as IConveyorResult ).__error__ === true;

			if ( isErrorResult ) {
				const shouldStop = this.handleTransformerError( result as IConveyorResult, settings );
				return { success: false, value: transformed, shouldStop };
			}

			return { success: true, value: result as T, shouldStop: false };
		} catch ( error: unknown ) {
			// Handle exceptions from transformer functions
			const errorResult: IConveyorResult = {
				__error__: true,
				message: 'Transformer execution failed',
				originalError: error instanceof Error ? error.message : String( error ),
				transformerId: entry.id
			};

			const shouldStop = this.handleTransformerError( errorResult, settings );
			return { success: false, value: transformed, shouldStop };
		}
	}

	/**
	 * @description Gets the count of registered transformers for a key
	 * @param {string} key Registry key
	 * @returns {number} Number of active transformers
	 */
	public static getTransformerCount( key: string ): number {
		ConveyorClass.cleanExpiredTransformers( key );
		const transformers = ConveyorClass.registry.get( key );
		return transformers ? transformers.length : 0;
	}

	/**
	 * @description Clears all transformers for a specific key
	 * @param {string} key Registry key to clear
	 */
	public static clearKey( key: string ): void {
		ConveyorClass.registry.delete( key );
	}

	/**
	 * @description Clears all transformers from the entire registry
	 */
	public static clearAll(): void {
		ConveyorClass.registry.clear();
	}

	/**
	 * @description Gets all registered keys
	 * @returns {string[]} Array of keys with registered transformers
	 */
	public static getKeys(): string[] {
		return Array.from( ConveyorClass.registry.keys() );
	}
}

/**
 * @description Factory function to create Conveyor instances
 * @param {string} key Identifier for this transformer pipeline
 * @returns {ConveyorClass} Conveyor instance
 */
const conveyorFactory = ( key: string ): ConveyorClass => {
	return new ConveyorClass( key );
};

// Attach static methods to the factory function
conveyorFactory.getTransformerCount = ConveyorClass.getTransformerCount.bind( ConveyorClass );
conveyorFactory.clearKey = ConveyorClass.clearKey.bind( ConveyorClass );
conveyorFactory.clearAll = ConveyorClass.clearAll.bind( ConveyorClass );
conveyorFactory.getKeys = ConveyorClass.getKeys.bind( ConveyorClass );

// Export the factory with proper type
export const Conveyor = conveyorFactory as IConveyorFactory;